import React, { useState } from 'react';
import { 
  multiFactor,
  MultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator
} from 'firebase/auth';
import { useLanguage } from '../../contexts/LanguageContext';

interface MFAVerificationProps {
  resolver: MultiFactorResolver;
  onSuccess: () => void;
  onCancel: () => void;
}

const MFAVerification: React.FC<MFAVerificationProps> = ({ resolver, onSuccess, onCancel }) => {
  const { t } = useLanguage();
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    try {
      setLoading(true);
      setError('');

      if (verificationCode.length !== 6) {
        setError(t('auth.mfaCodeLengthError'));
        return;
      }

      const selectedHint = resolver.hints[0];
      
      let multiFactorAssertion;
      
      if (selectedHint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
        multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
          selectedHint.uid,
          verificationCode
        );
      } else if (selectedHint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
        setError(t('auth.mfaSmsNotSupported'));
        return;
      } else {
        setError(t('auth.mfaUnknownFactor'));
        return;
      }

      await resolver.resolveSignIn(multiFactorAssertion);
      onSuccess();
    } catch (err: any) {
      console.error('Error verifying MFA:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError(t('auth.mfaInvalidCode'));
      } else if (err.code === 'auth/code-expired') {
        setError(t('auth.mfaCodeExpired'));
      } else {
        setError(t('auth.mfaVerificationError') + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && verificationCode.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('auth.mfaTitle')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('auth.mfaSubtitle')}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.verificationCode')}
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyPress={handleKeyPress}
              placeholder="000000"
              maxLength={6}
              autoFocus
              className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2 text-center">
              {t('auth.codeChangesEvery')}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleVerify}
              disabled={loading || verificationCode.length !== 6}
              className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-3 rounded-lg hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 transition-all font-medium"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('auth.verifying')}
                </div>
              ) : (
                t('auth.login')
              )}
            </button>
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('auth.cancel')}
            </button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>{t('auth.mfaTipLabel')}</strong> {t('auth.mfaTip')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;
